import React, { useState } from 'react';
import { Copy, Check, Code, Monitor, Smartphone, Globe } from 'lucide-react';
import './EmbedAgent.css';

const EmbedAgent = () => {
  const [selectedFramework, setSelectedFramework] = useState('iframe');
  const [selectedType, setSelectedType] = useState('inline');
  const [copied, setCopied] = useState(false);

  const agentId = '1'; // This should come from props or context
  // Use relative URL for production, absolute for development
  const isDevelopment = window.location.hostname === 'localhost';
  const serverUrl = isDevelopment ? 'https://localhost:5001' : '';

  const embedCodes = {
    iframe: {
      inline: `<iframe 
  src="${serverUrl}/embed.html?agentId=${agentId}" 
  width="100%" 
  height="500" 
  frameborder="0"
  style="border-radius: 8px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
</iframe>`,
      widget: `<script src="${serverUrl}/agent-widget.js"></script>
<agent-chat-widget 
  agent-id="${agentId}" 
  is-popup="true">
</agent-chat-widget>`
    },
    react: {
      inline: `import React from 'react';

const AgentChat = () => {
  return (
    <iframe 
      src="${serverUrl}/embed.html?agentId=${agentId}" 
      width="100%" 
      height="500" 
      frameBorder="0"
      style={{ borderRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
    />
  );
};

export default AgentChat;`,
      widget: `import React, { useEffect } from 'react';

const AgentWidget = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '${serverUrl}/agent-widget.js';
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <agent-chat-widget 
      agent-id="${agentId}" 
      is-popup="true">
    </agent-chat-widget>
  );
};

export default AgentWidget;`
    },
    vue: {
      inline: `<template>
  <iframe 
    :src="embedUrl" 
    width="100%" 
    height="500" 
    frameborder="0"
    style="border-radius: 8px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
  </iframe>
</template>

<script>
export default {
  data() {
    return {
      agentId: '${agentId}',
      serverUrl: '${serverUrl}'
    }
  },
  computed: {
    embedUrl() {
      return \`\${this.serverUrl}/embed.html?agentId=\${this.agentId}\`;
    }
  }
}
</script>`,
      widget: `<template>
  <agent-chat-widget 
    :agent-id="agentId" 
    is-popup="true">
  </agent-chat-widget>
</template>

<script>
export default {
  data() {
    return {
      agentId: '${agentId}'
    }
  },
  mounted() {
    const script = document.createElement('script');
    script.src = '${serverUrl}/agent-widget.js';
    document.head.appendChild(script);
  }
}
</script>`
    }
  };

  const handleCopy = async () => {
    const code = embedCodes[selectedFramework][selectedType];
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const currentCode = embedCodes[selectedFramework][selectedType];

  return (
    <div className="embed-agent" data-theme="dark">
      <div className="embed-header">
      </div>

      <div className="embed-options">
        <div className="option-group">
          <label>Framework</label>
          <div className="option-buttons">
            {[
              { id: 'iframe', label: 'HTML/Iframe', icon: Globe },
              { id: 'react', label: 'React', icon: Code },
              { id: 'vue', label: 'Vue.js', icon: Code }
            ].map(option => (
              <button
                key={option.id}
                className={`option-button ${selectedFramework === option.id ? 'active' : ''}`}
                onClick={() => setSelectedFramework(option.id)}
              >
                <option.icon size={16} />
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="option-group">
          <label>Display Type</label>
          <div className="option-buttons">
            {[
              { id: 'inline', label: 'Inline', icon: Monitor, description: 'Embedded in page' },
              { id: 'widget', label: 'Floating Widget', icon: Smartphone, description: 'Floating chat button' }
            ].map(option => (
              <button
                key={option.id}
                className={`option-button ${selectedType === option.id ? 'active' : ''}`}
                onClick={() => setSelectedType(option.id)}
              >
                <option.icon size={16} />
                <div className="button-content">
                  <span>{option.label}</span>
                  <small>{option.description}</small>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="code-section">
        <div className="code-header">
          <h3>Code</h3>
          <button 
            className={`copy-button ${copied ? 'copied' : ''}`}
            onClick={handleCopy}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Copied!' : 'Copy Code'}
          </button>
        </div>
        <pre className="code-block">
          <code>{currentCode}</code>
        </pre>
      </div>

      <div className="preview-section">
        <h3>Preview</h3>
        <div className="preview-container">
          {selectedType === 'inline' ? (
            <iframe 
              src={`${serverUrl}/embed.html?agentId=${agentId}`}
              width="100%" 
              height="400" 
              frameBorder="0"
              title="Agent Chat Preview"
              style={{ borderRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
            />
          ) : (
            <div className="widget-preview">
              <div className="widget-demo">
                <div className="floating-button">
                  <span>💬</span>
                </div>
                <p>Floating chat widget will appear in bottom-right corner</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmbedAgent;
